import React from 'react';
import { Drawer, Card, CardContent, Box, Typography, Pagination, Input, InputAdornment, IconButton } from '@mui/material';
import MainCard from 'components/MainCard';
import ExtendedIconButton from 'components/@extended/IconButton';
import { CloseCircleOutlined } from '@ant-design/icons';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import SimpleBar from 'components/third-party/SimpleBar';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useState, useEffect, useRef } from 'react';
import { searchChatThreads } from 'services/agentService';

const HistoryDrawer = ({
  open,
  onClose,
  threads = [],
  loading = false,
  onOpenThread,
  formatDateTime,
  page = 1,
  numPages = 1,
  total = 0,
  pageSize = 8,
  onChangePage
}) => {
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchNumPages, setSearchNumPages] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const inputRef = useRef(null);
  const paperRef = useRef(null);

  // Focus input when entering search mode
  useEffect(() => {
    if (isSearchMode) {
      setTimeout(() => {
        try {
          inputRef.current && inputRef.current.focus();
        } catch (_) {
          /* ignore */
        }
      }, 0);
    } else {
      // Reset search state when exiting
      setSearchTerm('');
      setSearchResults([]);
      setSearchPage(1);
      setSearchNumPages(1);
      setSearchTotal(0);
    }
  }, [isSearchMode]);

  // Close drawer on any outside click while allowing underlying click-through
  useEffect(() => {
    if (!open) return;
    const handleGlobalMouseDown = (e) => {
      try {
        const paperEl = paperRef.current;
        if (paperEl && !paperEl.contains(e.target)) {
          onClose && onClose();
        }
      } catch (_) {
        // ignore
      }
    };
    document.addEventListener('mousedown', handleGlobalMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown, true);
    };
  }, [open, onClose]);

  // Debounced search
  useEffect(() => {
    if (!isSearchMode) return;
    const term = (searchTerm || '').trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchNumPages(1);
      setSearchTotal(0);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const {
          items,
          page: p,
          num_pages,
          total: t
        } = await searchChatThreads({
          q: term,
          page: searchPage,
          page_size: pageSize || 8
        });
        if (cancelled) return;
        setSearchResults(Array.isArray(items) ? items : []);
        setSearchPage(Number(p) || 1);
        setSearchNumPages(Number(num_pages) || 1);
        setSearchTotal(Number(t) || (Array.isArray(items) ? items.length : 0));
      } catch (e) {
        if (!cancelled) {
          setSearchResults([]);
          setSearchNumPages(1);
          setSearchTotal(0);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isSearchMode, searchTerm, searchPage, pageSize]);

  const effectiveLoading = isSearchMode ? searchLoading : loading;
  const effectiveItems = isSearchMode ? searchResults : threads;
  const effectivePage = isSearchMode ? searchPage : page;
  const effectiveNumPages = isSearchMode ? searchNumPages : numPages;
  const effectiveTotal = isSearchMode ? searchTotal : total;

  const handleToggleSearch = () => {
    setIsSearchMode((prev) => !prev);
  };

  const handleChangePage = (p) => {
    if (isSearchMode) {
      setSearchPage(p);
    } else {
      onChangePage && onChangePage(p);
    }
  };

  return (
    <Drawer
      sx={{ zIndex: 1500 }}
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        BackdropProps: {
          sx: {
            backgroundColor: 'transparent',
            pointerEvents: 'none'
          }
        }
      }}
      PaperProps={{
        ref: paperRef,
        sx: {
          width: 400,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <MainCard
        title={
          isSearchMode ? (
            <Input
              inputRef={inputRef}
              placeholder="Search threads…"
              value={searchTerm}
              onChange={(e) => {
                setSearchPage(1);
                setSearchTerm(e.target.value);
              }}
              sx={{
                width: '100%',
                height: 36,
                '& .MuiInputBase-input': { py: 0.5 },
                // soften default underline colors
                '&:before': { borderBottomColor: 'divider' },
                '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'text.primary' }
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
              endAdornment={
                (searchTerm || '').trim().length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      size="small"
                      edge="end"
                      onClick={() => {
                        setSearchTerm('');
                        setSearchPage(1);
                        setSearchResults([]);
                        setSearchNumPages(1);
                        setSearchTotal(0);
                        // Also exit search mode per requirement
                        setIsSearchMode(false);
                        try {
                          inputRef.current && inputRef.current.blur && inputRef.current.blur();
                        } catch (_) {
                          /* ignore */
                        }
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          ) : (
            'Chats History'
          )
        }
        sx={{
          border: 'none',
          borderRadius: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          '& .MuiCardHeader-root': {
            color: 'text.secondary',
            bgcolor: 'secondary',
            '& .MuiTypography-root': { fontSize: '1rem' }
          }
        }}
        content={false}
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ExtendedIconButton
              shape="rounded"
              size="small"
              onClick={handleToggleSearch}
              aria-label={isSearchMode ? 'Close search' : 'Search threads'}
              sx={{ color: isSearchMode ? 'primary.main' : 'text.secondary' }}
            >
              <SearchOutlinedIcon style={{ fontSize: '1.15rem' }} />
            </ExtendedIconButton>
            <ExtendedIconButton shape="rounded" size="small" onClick={onClose} sx={{ color: 'primary.main' }}>
              <CloseCircleOutlined style={{ fontSize: '1.15rem' }} />
            </ExtendedIconButton>
          </Box>
        }
      >
        <SimpleBar sx={{ px: 1.5, pb: 1.5, flex: 1, minHeight: 0 }}>
          <Box mt={0} pb={0}>
            {effectiveLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Loading…
              </Typography>
            ) : Array.isArray(effectiveItems) && effectiveItems.length === 0 ? (
              isSearchMode ? (
                (searchTerm || '').trim().length < 3 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Type at least 3 characters to search.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No results found.
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No threads yet.
                </Typography>
              )
            ) : (
              (Array.isArray(effectiveItems) ? effectiveItems : []).map((t) => (
                <Card
                  key={t.id}
                  elevation={0}
                  sx={{
                    mb: 0.75,
                    borderRadius: 0,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: (theme) => theme.palette.background.paper,
                    transition: 'background-color 0.18s ease, transform 0.02s ease',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover
                    },
                    '&:active': { transform: 'translateY(1px)' }
                  }}
                  onClick={() => onOpenThread && onOpenThread(t.id)}
                  role="button"
                >
                  <CardContent sx={{ p: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SmsRoundedIcon sx={{ mr: 1.25, fontSize: 24, color: 'text.secondary' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {t.title || t.name || 'Untitled thread'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatDateTime ? formatDateTime(t.last_message_at || t.updated_at || t.created_at) : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </SimpleBar>
        <Box
          sx={{
            p: 1.5,
            pt: 0.75,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {effectiveTotal ? `${Math.min((effectivePage || 1) * (pageSize || 8), effectiveTotal)} of ${effectiveTotal}` : ''}
          </Typography>
          <Pagination
            size="small"
            shape="rounded"
            color="primary"
            count={Math.max(1, effectiveNumPages)}
            page={Math.max(1, Math.min(effectivePage || 1, Math.max(1, effectiveNumPages)))}
            onChange={(_, p) => handleChangePage(p)}
            disabled={
              (isSearchMode ? searchLoading : loading) || effectiveNumPages <= 1 || (isSearchMode && (searchTerm || '').trim().length < 3)
            }
          />
        </Box>
      </MainCard>
    </Drawer>
  );
};

export default HistoryDrawer;
